import {
  BaseModel,
  QueryClientEventWatchable,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import type {
  ReadonlyEventWatchable,
  ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework/watchable';
import type { QueryClient } from '@tanstack/react-query';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { MultiKeyUrlParamWatchable } from '@cotera/client/app/etc/url-param-watchable';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';

export type TriggerListItem = {
  /** `workflow_triggers_*` row id (`ResourceType.WorkflowTrigger` entity id). */
  id: string;
  /** Bound scheduled-agent workflow for `/triggers/:id` detail when present. */
  workflowId: string | null;
  name: string;
  kind: 'cron' | 'webhook' | 'event' | 'dataset' | string;
  schedule: string;
  enabled: boolean;
  agentId: string | null;
  agentTitle: string | null;
  status: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  folderName: string | null;
  folderScope: 'user' | 'org' | 'external' | null;
  /** Free-form listen name when `kind` is `event`. */
  eventName: string | null;
  /** Webhook source app (e.g. `hubspot`); `generic` for a custom webhook, null for cron. */
  webhookProviderKey: string | null;
  /**
   * Address an `email` webhook listens on. It *is* the trigger — a run per
   * message delivered there — so the row is named by it.
   */
  inboundEmailAddress: string | null;
  /**
   * Which of the provider's events this webhook listens for, when the provider
   * offers a choice. Resolved against the provider catalog for display.
   */
  webhookEventKey: string | null;
  /** Watched dataset when `kind` is `dataset`. */
  datasetId: string | null;
  /** What the dataset trigger watches for when `kind` is `dataset`. */
  datasetCriterion: DatasetTriggerCriterion | null;
  /** Whether the dataset trigger fires on its own, or only when asked. */
  datasetRunMode: DatasetTriggerRunMode | null;
};

export type TriggersViewMode = 'list' | 'calendar';

/**
 * Multi-select category filter for the grouped Workflows view. "Scheduled" =
 * cron triggers, "webhook" = "From an app" triggers, "event" = workflow event
 * listeners, "dataset" = dataset-change watchers, "standalone" = workflows with
 * no trigger. None checked = show all.
 */
export type TriggerCategoryFilter = {
  scheduled: boolean;
  webhook: boolean;
  event: boolean;
  dataset: boolean;
  standalone: boolean;
};

const CATEGORY_KEYS = [
  'scheduled',
  'webhook',
  'event',
  'dataset',
  'standalone',
] as const;

export type TriggersListParamsState = {
  page: number;
  limit: number;
  search: string;
  view: TriggersViewMode;
  category: TriggerCategoryFilter;
};

export const DEFAULT_TRIGGERS_LIST_PARAMS: TriggersListParamsState = {
  page: 1,
  limit: 100,
  search: '',
  view: 'list',
  category: {
    scheduled: false,
    webhook: false,
    event: false,
    dataset: false,
    standalone: false,
  },
};

/**
 * Maps the category filter to the server-side `triggerKind` param. Only filters
 * server-side when exactly one of the trigger categories is visible — with
 * more than one (or neither, when only "standalone" is checked) we need every
 * kind, so no filter. "standalone" doesn't map to a trigger kind (it's a
 * separate workflow source), so it never narrows the trigger query on its own.
 */
export function categoryToTriggerKind(
  category: TriggerCategoryFilter
): 'cron' | 'webhook' | 'event' | 'dataset' | undefined {
  const anyChecked =
    category.scheduled ||
    category.webhook ||
    category.event ||
    category.dataset ||
    category.standalone;
  const scheduledVisible = !anyChecked || category.scheduled;
  const webhookVisible = !anyChecked || category.webhook;
  const eventVisible = !anyChecked || category.event;
  const datasetVisible = !anyChecked || category.dataset;
  const kinds: Array<'cron' | 'webhook' | 'event' | 'dataset'> = [];
  if (scheduledVisible) {
    kinds.push('cron');
  }
  if (webhookVisible) {
    kinds.push('webhook');
  }
  if (eventVisible) {
    kinds.push('event');
  }
  if (datasetVisible) {
    kinds.push('dataset');
  }
  if (kinds.length === 1) {
    return kinds[0];
  }
  return undefined;
}

function parseCategory(raw: string | null): TriggerCategoryFilter {
  const selected = new Set((raw ?? '').split(',').filter((s) => s !== ''));
  return {
    scheduled: selected.has('scheduled'),
    webhook: selected.has('webhook'),
    event: selected.has('event'),
    dataset: selected.has('dataset'),
    standalone: selected.has('standalone'),
  };
}

function serializeCategory(category: TriggerCategoryFilter): string {
  return CATEGORY_KEYS.filter((k) => category[k]).join(',');
}

function categoryEquality(
  a: TriggerCategoryFilter,
  b: TriggerCategoryFilter
): boolean {
  return (
    a.scheduled === b.scheduled &&
    a.webhook === b.webhook &&
    a.event === b.event &&
    a.dataset === b.dataset &&
    a.standalone === b.standalone
  );
}

function parsePage(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_TRIGGERS_LIST_PARAMS.page;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 1
    ? Math.floor(n)
    : DEFAULT_TRIGGERS_LIST_PARAMS.page;
}

function parseLimit(s: string): number {
  if (s === '' || s === null || s === undefined) {
    return DEFAULT_TRIGGERS_LIST_PARAMS.limit;
  }
  const n = Number(s);
  return Number.isFinite(n)
    ? Math.min(100, Math.max(1, Math.floor(n)))
    : DEFAULT_TRIGGERS_LIST_PARAMS.limit;
}

function parseTriggersListParams(
  params: URLSearchParams
): TriggersListParamsState {
  const viewRaw = params.get('view');
  return {
    page: parsePage(params.get('page') ?? ''),
    limit: parseLimit(params.get('limit') ?? ''),
    search: params.get('search') ?? DEFAULT_TRIGGERS_LIST_PARAMS.search,
    view: viewRaw === 'calendar' ? 'calendar' : 'list',
    category: parseCategory(params.get('category')),
  };
}

function serializeTriggersListParams(
  s: TriggersListParamsState
): Record<string, string> {
  return {
    page: s.page === DEFAULT_TRIGGERS_LIST_PARAMS.page ? '' : String(s.page),
    limit: String(s.limit),
    search: s.search,
    view: s.view === DEFAULT_TRIGGERS_LIST_PARAMS.view ? '' : s.view,
    category: serializeCategory(s.category),
  };
}

function triggersListParamsEquality(
  a: TriggersListParamsState,
  b: TriggersListParamsState
): boolean {
  return (
    a.page === b.page &&
    a.limit === b.limit &&
    a.search === b.search &&
    a.view === b.view &&
    categoryEquality(a.category, b.category)
  );
}

export type TriggersListData = {
  items: TriggerListItem[];
  total: number;
};

function triggersDataEquality(
  a: TriggersListData | null,
  b: TriggersListData | null
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (a.total !== b.total || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (
      a.items[i]?.id !== b.items[i]?.id ||
      a.items[i]?.workflowId !== b.items[i]?.workflowId ||
      a.items[i]?.name !== b.items[i]?.name ||
      a.items[i]?.schedule !== b.items[i]?.schedule ||
      a.items[i]?.enabled !== b.items[i]?.enabled ||
      a.items[i]?.agentId !== b.items[i]?.agentId ||
      a.items[i]?.agentTitle !== b.items[i]?.agentTitle ||
      a.items[i]?.status !== b.items[i]?.status ||
      a.items[i]?.folderName !== b.items[i]?.folderName ||
      a.items[i]?.folderScope !== b.items[i]?.folderScope ||
      a.items[i]?.webhookProviderKey !== b.items[i]?.webhookProviderKey ||
      a.items[i]?.inboundEmailAddress !== b.items[i]?.inboundEmailAddress ||
      a.items[i]?.webhookEventKey !== b.items[i]?.webhookEventKey ||
      a.items[i]?.datasetId !== b.items[i]?.datasetId ||
      a.items[i]?.datasetCriterion !== b.items[i]?.datasetCriterion ||
      a.items[i]?.datasetRunMode !== b.items[i]?.datasetRunMode
    ) {
      return false;
    }
  }
  return true;
}

type TriggersListModelProps = {
  orgId: string;
  queryClient: QueryClient;
  client: TenantedClient;
  urlAdapter: UrlParamAdapter;
  initialSearchParams: URLSearchParams;
};

export class TriggersListModel extends BaseModel {
  private readonly _triggersDataEventWatchable: ReadonlyEventWatchable<TriggersListData | null>;
  private readonly _listParamsUrl: MultiKeyUrlParamWatchable<TriggersListParamsState>;
  private readonly _listParams: ReadonlyWatchable<TriggersListParamsState>;

  constructor(props: TriggersListModelProps) {
    super('triggers-list');

    const { client, queryClient, orgId } = props;

    this._listParamsUrl =
      new MultiKeyUrlParamWatchable<TriggersListParamsState>(
        ['page', 'limit', 'search', 'view', 'category'],
        {
          default: DEFAULT_TRIGGERS_LIST_PARAMS,
          parse: parseTriggersListParams,
          serialize: serializeTriggersListParams,
          equalityFn: triggersListParamsEquality,
        },
        props.urlAdapter,
        parseTriggersListParams(props.initialSearchParams)
      );
    this._listParams = this._listParamsUrl.asWatchable();

    this._triggersDataEventWatchable = QueryClientEventWatchable.for<
      TriggersListData | null,
      { resourceType?: string }
    >({
      queryClient,
      queryKeys: [
        [orgId, 'resource', 'listPage', ResourceType.WorkflowTrigger],
        [orgId, 'resource', 'listPage', ResourceType.Workflow],
      ],
      event: BroadcastEventType.ResourceUpdated,
      debounceMs: 500,
      runOnMount: true,
      initialValue: null,
      equalityFn: triggersDataEquality,
      filter: (payload) =>
        payload?.resourceType === 'workflow_trigger' ||
        payload?.resourceType === 'workflow',
      fn: async (_payload, get) => {
        const { page, limit, search, category } = get(this._listParams);
        const triggerKind = categoryToTriggerKind(category);
        const allItems: TriggerListItem[] = [];
        let total = 0;

        for (let p = 1; p <= page; p++) {
          const offset = (p - 1) * limit;
          const out = await queryClient.fetchQuery({
            queryKey: queryKeys.resource.listPage({
              orgId,
              type: ResourceType.WorkflowTrigger,
              limit,
              offset,
              ...(search !== undefined && search !== '' && { search }),
              ...(triggerKind !== undefined && { triggerKind }),
            }),
            staleTime: RESOURCE_LIST_FETCH_STALE_MS,
            queryFn: async () =>
              Assert.assertOk(
                await client.resource.list({
                  type: ResourceType.WorkflowTrigger,
                  limit,
                  offset,
                  ...(search !== undefined && search !== '' && { search }),
                  ...(triggerKind !== undefined && { triggerKind }),
                })
              ),
          });
          if (out.type !== 'workflow_trigger') {
            break;
          }
          total = out.total;
          const triggers = out.items.map(
            (row): TriggerListItem => ({
              id: row.id,
              workflowId: row.workflowId ?? null,
              name:
                row.name ??
                row.cronSchedule ??
                (row.triggerKind === 'webhook'
                  ? 'Webhook trigger'
                  : row.triggerKind === 'event'
                  ? row.eventName ?? 'Event trigger'
                  : row.triggerKind === 'dataset'
                  ? 'Dataset trigger'
                  : 'Trigger'),
              kind: row.triggerKind,
              schedule: row.cronSchedule ?? '',
              enabled: row.enabled ?? true,
              agentId: row.agentId ?? null,
              agentTitle: row.agentTitle ?? null,
              status: row.status,
              createdAt: new Date(row.createdAt),
              updatedAt: new Date(row.updatedAt),
              folderName: row.folderName ?? null,
              folderScope: row.folderScope ?? null,
              eventName: row.eventName ?? null,
              webhookProviderKey: row.webhookProviderKey ?? null,
              inboundEmailAddress: row.inboundEmailAddress ?? null,
              webhookEventKey: row.webhookEventKey ?? null,
              datasetId: row.datasetId ?? null,
              datasetCriterion: row.datasetCriterion ?? null,
              datasetRunMode: row.datasetRunMode ?? null,
            })
          );
          allItems.push(...triggers);
          if (out.items.length < limit) {
            break;
          }
        }

        return { items: allItems, total };
      },
    });
  }

  get listParamsWatchable(): ReadonlyWatchable<TriggersListParamsState> {
    return this._listParams;
  }

  get triggersDataWatchable(): ReadonlyEventWatchable<TriggersListData | null> {
    return this._triggersDataEventWatchable;
  }

  syncFromUrl(searchParams: URLSearchParams): void {
    this._listParamsUrl.syncFromUrl(searchParams);
    void this._triggersDataEventWatchable.refresh();
  }

  loadPage(page: number, take: number): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, page, limit: take });
  }

  setSearch(search: string): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, search, page: 1 });
  }

  setViewMode(view: TriggersViewMode): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, view });
  }

  setCategory(category: TriggerCategoryFilter): void {
    const current = this._listParamsUrl.snapshot();
    this._listParamsUrl.set({ ...current, category, page: 1 });
  }

  dispose(): void {
    this._triggersDataEventWatchable.unsubscribe();
    this._listParamsUrl.dispose();
  }
}

export const useTriggersListModel = (): TriggersListModel => {
  const model = useInScopeModel(TriggersListModel);
  if (model === null) {
    throw new Error('TriggersListModel not found in scope');
  }
  return model;
};
